import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import ProjectImagesLightbox from "./ProjectImagesLightbox";

export default function ServiceImagesGallery({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  useEffect(() => {
    if (images.length <= 1 || paused) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [images.length, paused]);

  if (!images.length) return null;

  const canNavigate = images.length > 1;
  const activeImage = images[activeIndex];

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <>
      <div
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-[#0a1a2f] shadow-lg"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="relative aspect-[16/10] w-full md:aspect-[2/1]">
          <img
            src={activeImage}
            alt={`${title} — ${activeIndex + 1}`}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a2f]/40 via-transparent to-transparent" />

          <button
            type="button"
            onClick={() => setLightboxIndex(activeIndex)}
            className="absolute inset-0 z-10 flex cursor-zoom-in items-center justify-center bg-[#0a1a2f]/0 opacity-0 transition-all hover:bg-[#0a1a2f]/25 hover:opacity-100"
            aria-label="View full size image"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#0a1a2f] shadow-lg">
              <ZoomIn className="h-4 w-4" />
              View image
            </span>
          </button>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white transition-colors hover:bg-[#1e6fcf]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next image"
                className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white transition-colors hover:bg-[#1e6fcf]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="absolute bottom-3 right-3 z-20 rounded-full bg-black/50 px-2.5 py-1 text-[10px] font-bold tracking-wider text-white">
                {activeIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>

      {canNavigate && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, index) => (
            <button
              key={`${src}-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                index === activeIndex
                  ? "border-[#1e6fcf] ring-2 ring-[#1e6fcf]/30"
                  : "border-slate-200 opacity-70 hover:opacity-100"
              }`}
              aria-label={`Show image ${index + 1}`}
            >
              <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {lightboxIndex !== null && (
        <ProjectImagesLightbox
          images={images}
          activeIndex={lightboxIndex}
          title={title}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
